import {Component, input, output, inject, computed, signal, effect} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {TenderDetailResponse, ProviderTenderRfcControllerService} from '@frontend/shared-api';
import {Button} from 'primeng/button';
import {firstValueFrom} from 'rxjs';
import {LoadingComponent} from '../../../../../shared/components/loading/loading.component';
import {Divider} from 'primeng/divider';
import {APP_ROUTES} from '../../../../../app.routes';
import {rxResource} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {ToastService} from '@frontend/toast';
import {ProviderSelectionWinnerComponent} from '../../../../customer/project-sequence/chapter/chapter-9-overall-result/step-9-2-project-decition/step-9-2-project-decision/components/provider-selection-winner/provider-selection-winner.component';
import {ProjectConclusionDeclineDialogService} from './project-conclusion-decline-dialog/project-conclusion-decline-dialog.service';
import {
  DeclineRfcTenderPhaseRequestParams,
  FinishRfcTenderPhaseRequestParams,
} from '@frontend/shared';
import {ProjectDecisionReasonVM} from '../../../../customer/project-sequence/chapter/chapter-9-overall-result/step-9-2-project-decition/step-9-2-project-decision/components/provider-selection-winner/provider-selection-winner.types';
import {SubsectionTitleComponent} from '../../../../../shared/components/header/subsection-title/subsection-title.component';
import {ConfirmationDialogService} from '../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-tender-project-rfc-project-conclusion',
  imports: [
    TranslocoPipe,
    Button,
    LoadingComponent,
    Divider,
    ProviderSelectionWinnerComponent,
    SubsectionTitleComponent,
  ],
  templateUrl: './tender-rfc-project-conclusion.component.html',
})
export class TenderRfcProjectConclusionComponent {
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly providerTenderRfcController = inject(ProviderTenderRfcControllerService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly declineDialogService = inject(ProjectConclusionDeclineDialogService);

  tenderDetails = input.required<TenderDetailResponse>();
  hasActivities = input<boolean>(true);
  nextTab = output<void>();

  protected readonly tenderId = computed(() => this.tenderDetails().tenderId);
  protected readonly projectId = computed(() => this.tenderDetails().projectId);

  protected readonly draftComment = signal<string | undefined>(undefined);

  protected readonly hrToolProviderDecisionResource = rxResource({
    params: () => ({tenderId: this.tenderId()}),
    stream: ({params: request}) =>
      this.providerTenderRfcController.getProjectDecisionContainer({tenderId: request.tenderId}),
  });

  constructor() {
    effect(() => {
      const remoteComment = this.contractProjectDecision()?.comment;
      if (remoteComment != null && this.draftComment() == null) {
        this.draftComment.set(remoteComment);
      }
    });
  }

  selectedProviderDetails = computed(() => {
    const container = this.hrToolProviderDecisionResource.value();
    return container?.winner;
  });

  contractProjectDecision = computed(
    () => this.hrToolProviderDecisionResource.value()?.contractProjectDecision,
  );

  selectedProviderFees = computed<ProjectDecisionReasonVM | null>(() => {
    const contractDecision = this.contractProjectDecision();
    if (!contractDecision) {
      return null;
    }
    const contractHrToolTotalFees = contractDecision.hrToolTotalFees;
    return {
      totalServiceFees: contractHrToolTotalFees.totalServiceFees,
      implementationFees: contractHrToolTotalFees.implementationFees,
      integrationSetupFees: contractHrToolTotalFees.integrationSetupFees,
      integrationOtherFees: contractHrToolTotalFees.integrationOtherFees,
      totalAllYearsFees: contractHrToolTotalFees.totalAllYearsFees,
      contractDurationMonths: contractDecision.contractDurationMonths,
      contractSigningDate: new Date(contractDecision.contractSigningDate),
      comment: contractDecision.comment ?? '',
    };
  });

  providerDeclined = computed(() => this.contractProjectDecision()?.providerDeclined);

  protected declinePhase() {
    this.declineDialogService.confirmDecline({
      initialComment: this.draftComment(),
      decline: (comment) => {
        this.draftComment.set(comment);
      },
      accept: async (comment) => {
        this.draftComment.set(undefined);
        try {
          const request: DeclineRfcTenderPhaseRequestParams = {
            tenderId: this.tenderId(),
            providerTenderRfcDeclineUpsertRequest: {
              comment: comment,
            },
          };

          await firstValueFrom(this.providerTenderRfcController.declineRfcTenderPhase(request));
          await this.router.navigate([APP_ROUTES.PROVIDER_TENDERS]);
          void this.toastService.showSuccessMessage();
        } catch (err) {
          console.error('Decline Phase failed:', err);
        }
      },
    });
  }

  finishPhase(): void {
    this.confirmDialogService.confirm({
      headerKey: 'provider.tenderWizard.phase.confirmation.header',
      messageKey: 'provider.tender.project.conclusion.confirmation.text',
      accept: async () => {
        try {
          const request: FinishRfcTenderPhaseRequestParams = {
            tenderId: this.tenderId(),
          };
          await firstValueFrom(this.providerTenderRfcController.finishRfcTenderPhase(request));
          await this.router.navigate([APP_ROUTES.PROVIDER_TENDERS]);
          void this.toastService.showSuccessMessage();
        } catch (err) {
          console.error('Finish Phase failed:', err);
        }
      },
    });
  }
}
